import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../App";
import { Button } from "../components/ui/button";
import { Mail, ArrowLeft, Loader2, Compass, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetToken, setResetToken] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email address");
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("/auth/forgot-password", { email });
      setSent(true);
      // In production, the token would be sent via email
      // For now, we capture it from the response for the reset flow
      if (res.data.reset_token) {
        setResetToken(res.data.reset_token);
      }
      toast.success("Reset instructions ready");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "#070B14" }}>
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A16207] flex items-center justify-center">
            <Compass className="w-5 h-5 text-[#070B14]" />
          </div>
          <span className="font-display text-2xl text-white">SoulGuide</span>
        </div>

        <div className="glass-card p-8 space-y-6" data-testid="forgot-password-page">
          {!sent ? (
            <>
              <div className="text-center">
                <h1 className="font-display text-2xl text-white">Reset Password</h1>
                <p className="text-white/50 font-body text-sm mt-1">
                  Enter your email and we'll help you reset your password
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <input
                    data-testid="forgot-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white font-body text-sm placeholder-white/30 focus:outline-none focus:border-[#D4AF37]/50"
                    autoFocus
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="btn-gold w-full py-5"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    "Send Reset Link"
                  )}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/20 flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="font-display text-xl text-white">Check Your Email</h2>
              <p className="text-white/50 font-body text-sm">
                If an account exists for <strong className="text-white/70">{email}</strong>, you'll receive reset instructions.
              </p>

              {resetToken && (
                <div className="mt-4 p-4 rounded-xl bg-[#D4AF37]/5 border border-[#D4AF37]/20">
                  <p className="text-[#D4AF37] text-xs font-body mb-2">Reset link ready:</p>
                  <Button
                    onClick={() => navigate(`/reset-password?token=${resetToken}`)}
                    className="btn-gold w-full"
                    data-testid="reset-link-btn"
                  >
                    Reset Password Now
                  </Button>
                </div>
              )}
            </div>
          )}

          <div className="text-center pt-2">
            <Link
              to="/"
              className="text-white/40 hover:text-white/60 font-body text-sm inline-flex items-center gap-1"
              data-testid="back-to-login-link"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
